/**
 * Bulk Scheduling Service
 *
 * Bridges the bulk content planner to the posts collection for organic content.
 * When content is scheduled through the Smart Schedule step, this service creates
 * the actual post documents that the scheduledPostsService polls for publishing.
 *
 * Organic Content Flow:
 *   SmartScheduleStep → bulkSchedulingService.scheduleOrganicPosts() → posts collection
 *
 * Paid Content Flow:
 *   SmartScheduleStep → DeployToFunnelModal → funnels collection → Platform APIs
 */

import {
    collection,
    doc,
    addDoc,
    getDoc,
    getDocs,
    updateDoc,
    deleteDoc,
    deleteField,
    setDoc,
    query,
    where,
    writeBatch,
    serverTimestamp
} from 'firebase/firestore';
import { db } from '../lib/firebase';

const POSTS_COLLECTION = 'posts';
const CONTENT_BRIEFS_COLLECTION = 'contentBriefs';
const POSTS_LOCAL_COLLECTION = 'postsLocal';

/**
 * Schedule organic posts by creating documents in the posts collection.
 * This bridges the gap between bulk planner scheduling and actual publishing.
 *
 * @param {Array} scheduledPosts - Array of scheduled post data from SmartScheduleStep
 *   Each item should have: { briefId, dateTime, time, platforms, quality }
 * @param {Array} briefs - Array of full brief objects with content data
 * @param {Object} brand - The brand object
 * @param {string} userId - The user ID performing the scheduling
 * @returns {Promise<{success: boolean, postIds: string[], errors: Array}>}
 */
export async function scheduleOrganicPosts(scheduledPosts, briefs, brand, userId) {
    if (!scheduledPosts || scheduledPosts.length === 0) {
        return { success: true, postIds: [], errors: [] };
    }

    if (!brand?.id) {
        throw new Error('Brand is required for scheduling organic posts');
    }

    if (!userId) {
        throw new Error('User ID is required for scheduling organic posts');
    }

    // Create a map of briefs for quick lookup
    const briefsMap = new Map();
    briefs.forEach(brief => {
        if (brief?.id) {
            briefsMap.set(brief.id, brief);
        }
    });

    // Filter for organic content only (exclude paid content types)
    const organicPosts = scheduledPosts.filter(sp => {
        const brief = briefsMap.get(sp.briefId);
        // Consider a post organic if contentType is not 'paid' or if contentType is not set
        return brief && brief.contentType !== 'paid';
    });

    if (organicPosts.length === 0) {
        console.log('No organic posts to schedule');
        return { success: true, postIds: [], errors: [] };
    }

    console.log(`Scheduling ${organicPosts.length} organic posts for brand ${brand.id}`);

    const postIds = [];
    const errors = [];
    const batch = writeBatch(db);
    const briefUpdates = [];

    for (const scheduledPost of organicPosts) {
        try {
            const brief = briefsMap.get(scheduledPost.briefId);
            if (!brief) {
                errors.push({
                    briefId: scheduledPost.briefId,
                    error: 'Brief not found'
                });
                continue;
            }

            // Build the post document matching the schema expected by scheduledPostsService
            const postData = buildPostFromBrief(brief, scheduledPost, brand, userId);

            // Create the post document
            const postRef = doc(collection(db, POSTS_COLLECTION));
            batch.set(postRef, postData);
            postIds.push(postRef.id);

            // Track brief update for deployment status
            briefUpdates.push({
                briefId: scheduledPost.briefId,
                postId: postRef.id
            });

        } catch (error) {
            console.error(`Failed to prepare post for brief ${scheduledPost.briefId}:`, error);
            errors.push({
                briefId: scheduledPost.briefId,
                error: error.message
            });
        }
    }

    // Commit the batch
    try {
        await batch.commit();
        console.log(`Successfully created ${postIds.length} posts in posts collection`);

        // Update briefs with deployment status
        for (const update of briefUpdates) {
            try {
                const briefRef = doc(db, CONTENT_BRIEFS_COLLECTION, update.briefId);
                await updateDoc(briefRef, {
                    deploymentStatus: 'scheduled_organic',
                    deployedPostId: update.postId,
                    deployedAt: serverTimestamp()
                });
            } catch (err) {
                console.warn(`Failed to update brief ${update.briefId} deployment status:`, err);
            }
        }

    } catch (error) {
        console.error('Failed to commit batch:', error);
        return {
            success: false,
            postIds: [],
            errors: [{ error: 'Batch commit failed: ' + error.message }]
        };
    }

    return {
        success: errors.length === 0,
        postIds,
        errors
    };
}

/**
 * Build a post document from a brief and scheduling data.
 * Matches the schema expected by scheduledPostsService and ContentScheduler.
 */
function buildPostFromBrief(brief, scheduledPost, brand, userId) {
    // Debug logging for carousel data flow
    console.log('[buildPostFromBrief] Brief data:', {
        briefId: brief.id,
        outputType: brief.outputType,
        contentFormat: brief.contentFormat,
        hasCarouselImages: !!brief.carouselImages,
        carouselImagesLength: brief.carouselImages?.length || 0,
        carouselImagesPreview: brief.carouselImages?.slice(0, 2).map(img => ({
            hasUrl: !!img.url,
            hasImageUrl: !!img.imageUrl,
            urlPreview: (img.url || img.imageUrl || '').substring(0, 50)
        }))
    });

    // Get the generated creative info from the brief
    const imageUrl = brief.generatedImageUrl ||
        brief.generatedAssets?.[0]?.url ||
        brief.brief?.imageUrl ||
        null;

    const thumbnailUrl = brief.generatedThumbnailUrl ||
        brief.generatedAssets?.[0]?.thumbnailUrl ||
        imageUrl;

    // Parse the scheduled date/time
    const scheduledDate = scheduledPost.dateTime instanceof Date
        ? scheduledPost.dateTime.toISOString()
        : scheduledPost.dateTime;

    // Get caption and hashtags from brief content
    const caption = brief.brief?.caption ||
        brief.brief?.post_copy ||
        brief.brief?.content ||
        '';

    // Format hashtags with # prefix
    const rawHashtags = brief.brief?.hashtags || brief.brief?.tags || [];
    const hashtags = Array.isArray(rawHashtags)
        ? rawHashtags.map(tag => tag.startsWith('#') ? tag : `#${tag}`).join(' ')
        : (typeof rawHashtags === 'string' ? rawHashtags : '');

    return {
        // Core identifiers
        creativeId: brief.generatedCreativeId || brief.id,
        briefId: brief.id,
        planId: brief.planId || null,

        // Brand info
        brandId: brand.id,
        brandName: brand.name,

        // Scheduling info
        platforms: scheduledPost.platforms || [],
        scheduledDate: scheduledDate,
        scheduledTime: scheduledPost.time || null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || brand.timezone || 'America/New_York',
        targetTimezone: brand.primaryMarket?.timezone || brand.timezone || 'America/New_York',

        // Status
        status: 'scheduled',

        // Source tracking
        source: 'bulk_planner',

        // Creative content
        creativeTitle: brief.title || brief.brief?.headline || 'Untitled',
        creativeType: brief.outputType || brief.contentFormat || 'image',
        imageUrl: imageUrl,
        thumbnailUrl: thumbnailUrl,
        caption: caption,
        hashtags: hashtags,

        // Carousel support - check carouselImages field (set by bulkGenerationService for carousel content)
        // Note: imageUrl (not url) is required by metaAdsService.publishToInstagram/publishToFacebookPage
        carouselImages: (() => {
            const mapped = brief.carouselImages?.length > 0
                ? brief.carouselImages.map(img => ({ imageUrl: img.url || img.imageUrl, thumbnailUrl: img.thumbnailUrl || img.url || img.imageUrl }))
                : null;
            console.log('[buildPostFromBrief] Mapped carousel images:', mapped?.length || 0, 'items');
            return mapped;
        })(),

        // Brief metadata
        pillarId: brief.pillarId || null,
        pillarName: brief.pillarName || null,
        funnelStage: brief.funnelStage || 'TOFU',
        contentType: brief.contentType || 'organic',

        // Quality tracking
        scheduleQuality: scheduledPost.quality || 'good',

        // Retry tracking (Phase 7)
        publishAttempts: 0,
        lastAttemptAt: null,
        lastError: null,

        // Approval workflow (Phase 7) - defaults to not required
        approvalStatus: 'not_required',
        approvedBy: null,
        approvedAt: null,
        rejectionReason: null,

        // Version history (Phase 9)
        versions: [],
        currentVersion: 1,

        // Timestamps
        createdAt: new Date().toISOString(),
        createdBy: userId,
        updatedAt: null
    };
}

/**
 * Cancel scheduled posts from the bulk planner.
 *
 * @param {string[]} postIds - Array of post IDs to cancel
 * @param {string} userId - The user ID performing the cancellation
 * @returns {Promise<{success: boolean, cancelledCount: number, errors: Array}>}
 */
export async function cancelScheduledPosts(postIds, userId) {
    if (!postIds || postIds.length === 0) {
        return { success: true, cancelledCount: 0, errors: [] };
    }

    const errors = [];
    let cancelledCount = 0;

    for (const postId of postIds) {
        try {
            const postRef = doc(db, POSTS_COLLECTION, postId);
            const postDoc = await getDoc(postRef);

            if (!postDoc.exists()) {
                errors.push({ postId, error: 'Post not found' });
                continue;
            }

            const post = postDoc.data();

            // Only allow cancellation of scheduled posts
            if (post.status !== 'scheduled') {
                errors.push({ postId, error: `Cannot cancel post with status: ${post.status}` });
                continue;
            }

            // Update status to cancelled
            await updateDoc(postRef, {
                status: 'cancelled',
                cancelledAt: serverTimestamp(),
                cancelledBy: userId
            });

            // Update the linked brief if it exists
            if (post.briefId) {
                try {
                    const briefRef = doc(db, CONTENT_BRIEFS_COLLECTION, post.briefId);
                    await updateDoc(briefRef, {
                        deploymentStatus: 'pending',
                        deployedPostId: null,
                        status: 'draft'
                    });
                } catch (err) {
                    console.warn(`Failed to update brief ${post.briefId} after cancellation:`, err);
                }
            }

            cancelledCount++;
        } catch (error) {
            console.error(`Failed to cancel post ${postId}:`, error);
            errors.push({ postId, error: error.message });
        }
    }

    return {
        success: errors.length === 0,
        cancelledCount,
        errors
    };
}

/**
 * Get all scheduled posts originating from a content plan.
 *
 * @param {string} planId - The content plan ID
 * @returns {Promise<Array>} Array of post objects
 */
export async function getScheduledPostsForPlan(planId) {
    if (!planId) {
        throw new Error('Plan ID is required');
    }

    const postsQuery = query(
        collection(db, POSTS_COLLECTION),
        where('planId', '==', planId),
        where('source', '==', 'bulk_planner')
    );

    const snapshot = await getDocs(postsQuery);
    return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
    }));
}

/**
 * Get all scheduled posts for a brand from the bulk planner.
 *
 * @param {string} brandId - The brand ID
 * @param {Object} options - Query options
 * @param {string} options.status - Filter by status (scheduled, published, cancelled, failed)
 * @param {Date} options.fromDate - Filter from date
 * @param {Date} options.toDate - Filter to date
 * @returns {Promise<Array>} Array of post objects
 */
export async function getBulkPlannerPosts(brandId, options = {}) {
    if (!brandId) {
        throw new Error('Brand ID is required');
    }

    let postsQuery = query(
        collection(db, POSTS_COLLECTION),
        where('brandId', '==', brandId),
        where('source', '==', 'bulk_planner')
    );

    if (options.status) {
        postsQuery = query(postsQuery, where('status', '==', options.status));
    }

    const snapshot = await getDocs(postsQuery);
    let posts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
    }));

    // Apply date filters in memory (Firestore doesn't support multiple range queries)
    if (options.fromDate) {
        const fromTime = options.fromDate.getTime();
        posts = posts.filter(p => new Date(p.scheduledDate).getTime() >= fromTime);
    }

    if (options.toDate) {
        const toTime = options.toDate.getTime();
        posts = posts.filter(p => new Date(p.scheduledDate).getTime() <= toTime);
    }

    // Sort by scheduled date
    posts.sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));

    return posts;
}

/**
 * Update a scheduled post (for editing after scheduling).
 *
 * @param {string} postId - The post ID
 * @param {Object} updates - Fields to update
 * @param {string} userId - The user ID performing the update
 * @returns {Promise<{success: boolean, version: number}>}
 */
export async function updateScheduledPost(postId, updates, userId) {
    if (!postId) {
        throw new Error('Post ID is required');
    }

    const postRef = doc(db, POSTS_COLLECTION, postId);
    const postDoc = await getDoc(postRef);

    if (!postDoc.exists()) {
        throw new Error('Post not found');
    }

    const currentPost = postDoc.data();

    // Allow updates to draft, scheduled, failed, and published posts
    // - draft: editing before scheduling
    // - scheduled: normal editing before publish
    // - failed: retry after fixing issues
    // - published: unpublish or republish operations
    const editableStatuses = ['draft', 'scheduled', 'failed', 'published'];
    if (!editableStatuses.includes(currentPost.status)) {
        throw new Error(`Cannot update post with status: ${currentPost.status}`);
    }

    // Clean updates - remove null values (Firestore doesn't allow null in arrays)
    // and prepare deletions separately
    const cleanedUpdates = {};
    const fieldsToDelete = [];

    for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === undefined) {
            // Mark for deletion instead of setting to null
            fieldsToDelete.push(key);
        } else {
            cleanedUpdates[key] = value;
        }
    }

    const newVersion = (currentPost.currentVersion || 1) + 1;

    // Helper to clean values for Firestore array storage
    // Firestore doesn't allow Timestamps or other special objects inside arrays
    const cleanValueForArray = (value) => {
        if (value === null || value === undefined) {
            return undefined; // Will be filtered out
        }
        // Handle Firestore Timestamp objects
        if (value && typeof value === 'object' && typeof value.toDate === 'function') {
            return value.toDate().toISOString();
        }
        // Handle Firestore Timestamp-like objects (seconds/nanoseconds)
        if (value && typeof value === 'object' && 'seconds' in value && 'nanoseconds' in value) {
            return new Date(value.seconds * 1000).toISOString();
        }
        // Handle Date objects
        if (value instanceof Date) {
            return value.toISOString();
        }
        // Handle arrays recursively
        if (Array.isArray(value)) {
            return value.map(cleanValueForArray).filter(v => v !== undefined);
        }
        // Handle nested objects recursively
        if (value && typeof value === 'object') {
            const cleaned = {};
            for (const [k, v] of Object.entries(value)) {
                const cleanedValue = cleanValueForArray(v);
                if (cleanedValue !== undefined) {
                    cleaned[k] = cleanedValue;
                }
            }
            return cleaned;
        }
        return value;
    };

    // Build snapshot without null values - clean the merged object
    const mergedSnapshot = { ...currentPost, ...cleanedUpdates };
    // Remove fields that are being deleted
    for (const field of fieldsToDelete) {
        delete mergedSnapshot[field];
    }
    // IMPORTANT: Remove versions from snapshot to prevent recursive nesting
    delete mergedSnapshot.versions;

    // Clean snapshot - remove null/undefined and convert Timestamps
    const cleanSnapshot = {};
    for (const [key, value] of Object.entries(mergedSnapshot)) {
        const cleanedValue = cleanValueForArray(value);
        if (cleanedValue !== undefined) {
            cleanSnapshot[key] = cleanedValue;
        }
    }

    // Build version history entry - clean old values too (may contain Timestamps)
    const changes = {};
    for (const [key, value] of Object.entries(cleanedUpdates)) {
        if (currentPost[key] !== value) {
            changes[key] = {
                old: cleanValueForArray(currentPost[key]) ?? 'none',
                new: cleanValueForArray(value) ?? value
            };
        }
    }

    // Track deletions in changes
    for (const field of fieldsToDelete) {
        if (currentPost[field] !== undefined) {
            changes[field] = {
                old: cleanValueForArray(currentPost[field]) ?? 'none',
                new: 'cleared'
            };
        }
    }

    const versionEntry = {
        version: newVersion,
        timestamp: new Date().toISOString(),
        changedBy: userId,
        changes,
        snapshot: cleanSnapshot
    };

    // Clean existing versions array (may contain Timestamps from previous saves)
    const cleanedExistingVersions = (currentPost.versions || []).map(v => cleanValueForArray(v));

    // Build the final update object
    const finalUpdate = {
        ...cleanedUpdates,
        versions: [...cleanedExistingVersions, versionEntry],
        currentVersion: newVersion,
        updatedAt: serverTimestamp()
    };

    // Add field deletions using deleteField()
    for (const field of fieldsToDelete) {
        finalUpdate[field] = deleteField();
    }

    // Update the post
    await updateDoc(postRef, finalUpdate);

    return {
        success: true,
        version: newVersion
    };
}

/**
 * Check if a brand has any pending organic posts from bulk planner.
 *
 * @param {string} brandId - The brand ID
 * @returns {Promise<{count: number, nextPost: Object|null}>}
 */
export async function getPendingOrganicPostsCount(brandId) {
    const posts = await getBulkPlannerPosts(brandId, { status: 'scheduled' });

    const now = new Date();
    const pendingPosts = posts.filter(p => new Date(p.scheduledDate) > now);

    return {
        count: pendingPosts.length,
        nextPost: pendingPosts[0] || null
    };
}

// ============================================================================
// LOCAL TESTING FLAG FUNCTIONS
// ============================================================================

/**
 * Check if a post is flagged for local testing.
 * Posts in postsLocal collection will only be published by localhost server.
 *
 * @param {string} postId - The post ID to check
 * @returns {Promise<boolean>} True if flagged for local testing
 */
export async function isPostFlaggedForLocalTesting(postId) {
    if (!postId) return false;

    try {
        const localDoc = await getDoc(doc(db, POSTS_LOCAL_COLLECTION, postId));
        return localDoc.exists();
    } catch (error) {
        console.error('Error checking local testing flag:', error);
        return false;
    }
}

/**
 * Set the local testing flag for a post.
 * When set, the post will only be published by the localhost server.
 *
 * @param {string} postId - The post ID to flag
 * @returns {Promise<{success: boolean}>}
 */
export async function setLocalTestingFlag(postId) {
    if (!postId) {
        throw new Error('Post ID is required');
    }

    try {
        await setDoc(doc(db, POSTS_LOCAL_COLLECTION, postId), {
            postId,
            createdAt: serverTimestamp()
        });

        return { success: true };
    } catch (error) {
        console.error('Error setting local testing flag:', error);
        throw error;
    }
}

/**
 * Clear the local testing flag for a post.
 * When cleared, the post will be published by the production server.
 *
 * @param {string} postId - The post ID to unflag
 * @returns {Promise<{success: boolean}>}
 */
export async function clearLocalTestingFlag(postId) {
    if (!postId) {
        throw new Error('Post ID is required');
    }

    try {
        await deleteDoc(doc(db, POSTS_LOCAL_COLLECTION, postId));
        return { success: true };
    } catch (error) {
        console.error('Error clearing local testing flag:', error);
        throw error;
    }
}

/**
 * Get all post IDs that are flagged for local testing.
 *
 * @returns {Promise<Set<string>>} Set of post IDs flagged for local testing
 */
export async function getLocalTestingPostIds() {
    try {
        const snapshot = await getDocs(collection(db, POSTS_LOCAL_COLLECTION));
        return new Set(snapshot.docs.map(doc => doc.id));
    } catch (error) {
        console.error('Error getting local testing post IDs:', error);
        return new Set();
    }
}

// ============================================================================
// UNPUBLISH FUNCTIONS
// ============================================================================

const API_BASE = 'http://localhost:3001';

/**
 * Unpublish a post - delete from platforms and reset to draft status.
 * Calls the backend API to delete from social platforms, then resets local status.
 *
 * @param {string} postId - The post ID to unpublish
 * @param {string} userId - The user ID performing the unpublish
 * @returns {Promise<{success: boolean, message: string, results: Object}>}
 */
export async function unpublishPost(postId, userId) {
    if (!postId) {
        throw new Error('Post ID is required');
    }

    if (!userId) {
        throw new Error('User ID is required');
    }

    try {
        const response = await fetch(`${API_BASE}/api/scheduler/unpublish/${postId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ userId })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to unpublish: ${response.status}`);
        }

        const data = await response.json();

        // Update the linked brief if it exists
        const postRef = doc(db, POSTS_COLLECTION, postId);
        const postDoc = await getDoc(postRef);

        if (postDoc.exists()) {
            const post = postDoc.data();
            if (post.briefId) {
                try {
                    const briefRef = doc(db, CONTENT_BRIEFS_COLLECTION, post.briefId);
                    await updateDoc(briefRef, {
                        deploymentStatus: 'pending',
                        deployedPostId: null,
                        status: 'draft'
                    });
                } catch (err) {
                    console.warn(`Failed to update brief ${post.briefId} after unpublish:`, err);
                }
            }
        }

        return {
            success: true,
            message: data.message,
            results: data.results
        };

    } catch (error) {
        console.error('Error unpublishing post:', error);
        throw error;
    }
}
